import * as React from 'react';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton, TextButtonSize } from '@creditornot/cb-button';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useWarehouseContext } from 'common/context/WarehouseContext';

import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { useWarehousesQuery } from '../../../hooks/useWarehousesQuery';
import { SearchInput } from '../Search/SearchInput';

const WarehouseSelectionDropdown = styled.div`
  min-width: 280px;
  max-height: 284px;
  padding: 8px;
  border-radius: 8px;
  overflow-y: scroll;
`;

const SearchContainer = styled.div`
  margin-bottom: 12px;
`;

const WarehouseSelectorMessageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const WarehouseOption = styled(MenuActionItem)`
  & + & {
    margin-top: 8px;
  }
`;

type Props = {
  textButtonSize?: TextButtonSize;
};

export const WarehouseSelector = ({ textButtonSize }: Props) => {
  const formatMessage = useFormatMessage();
  const { warehouse: selectedWarehouse, onWarehouseChange } = useWarehouseContext();
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const selectedWarehouseId = selectedWarehouse?.id;
  const selectedWarehouseName = selectedWarehouse?.name;

  const { data: filteredWarehousesData, error: loadingFilteredWarehousesError } =
    useWarehousesQuery({
      q: searchQuery || undefined,
    });
  const filteredWarehouses = filteredWarehousesData?.items;

  return (
    <Popover
      offset={4}
      placement="bottom-start"
      renderArrow={false}
      zIndex={1}
      content={({ close }) => {
        return (
          <WarehouseSelectionDropdown>
            <SearchContainer>
              <SearchInput
                value={searchQuery}
                autoFocus={true}
                onChange={(value) => {
                  setSearchQuery(value);
                }}
                id="warehouse-search-input"
              />
            </SearchContainer>
            {loadingFilteredWarehousesError ? (
              <WarehouseSelectorMessageContainer>
                {formatMessage('components.navigation_bar.error_loading_warehouses')}
              </WarehouseSelectorMessageContainer>
            ) : !filteredWarehouses?.length ? (
              <WarehouseSelectorMessageContainer>
                {formatMessage('components.navigation_bar.warehouses_not_found')}
              </WarehouseSelectorMessageContainer>
            ) : (
              (filteredWarehouses || []).map((warehouse) => (
                <WarehouseOption
                  key={warehouse.id}
                  variant="black"
                  selected={warehouse.id === selectedWarehouseId}
                  onClick={() => {
                    onWarehouseChange?.(warehouse.id);
                    close();
                    setSearchQuery('');
                  }}
                >
                  {warehouse.name}
                </WarehouseOption>
              ))
            )}
          </WarehouseSelectionDropdown>
        );
      }}
    >
      {({ refProps }) => (
        <TextButton
          data-test-id="select-warehouse-button"
          variant={selectedWarehouseId ? 'brand' : 'lightGrey'}
          size={textButtonSize ?? 'small'}
          icon={icons.chevronDown}
          iconPosition="end"
          {...refProps}
        >
          {selectedWarehouseName || formatMessage('components.navigation_bar.select_warehouse')}
        </TextButton>
      )}
    </Popover>
  );
};
